import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Pagination,
  Skeleton,
  Stack,
  TablePagination,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AdvisorMapsTableSkeleton from "./AdvisorMapsTableSkeleton";
import {
  selectUserData,
  selectUserToken,
} from "../../../../features/userSlice";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import PersistentErrorSnackbar from "../../../PersistentErrorSnackbar/PersistentErrorSnackbar";
import { AdvisorMapsEndpointLogic } from "../../../../logic/DataCollection/Advisor/AdvisorMapsEndPointLogic";
import { advisorCanonicalMetrics } from "../helpers/advisorStaticData";

/** Gap rule the API returns under each canonical metric key on a map. */
export interface AdvisorMapMetricRule {
  id: number;
  rule_title: string | null;
  target_field: string | null;
  expression_sql: string | null;
  description: string | null;
  dependency_field: string | null;
}

export interface AdvisorMapApiRow {
  id: number;
  name: string;
  backend_interface_id: number;
  backend_interface_name: string;
  destination_table_id: number;
  destination_table: string;
  brands_connected: number;
  // the canonical metric keys (ftd, qftd, ...) each carry a gap rule object
  [metricKey: string]: any;
}

/** One row of the "Brands Using Map" dialog. */
export interface AdvisorMapBrand {
  brand_id: number;
  brand_name: string;
}

interface Props {
  advisorMapsData: AdvisorMapApiRow[];
}

type SortDirection = "asc" | "desc";
type BrandSortKey = "brand_name" | "brand_id";

const tableHeaders = [
  { id: 0, KEY: "name", label: "Name", sortable: true },
  { id: 1, KEY: "id", label: "ID", sortable: true },
  {
    id: 2,
    KEY: "destination_table",
    label: "Destination Table",
    sortable: true,
  },
  {
    id: 3,
    KEY: "brands_connected",
    label: "Brands Using Map",
    sortable: true,
  },
  { id: 4, KEY: "derivations", label: "Derivations", sortable: false },
  { id: 5, KEY: "actions", label: "Quick Actions", sortable: false },
];

/**
 * Every metric carrying a rule_title gets its own line - including the
 * source-driven ones, which come back as "Value is updated from Unifier".
 * A map only collapses to "All Metrics from Source" when every rule_title is
 * null.
 */
const getMetricRules = (row: AdvisorMapApiRow) =>
  advisorCanonicalMetrics
    .map((metric) => ({ metric, rule: row?.[metric] as AdvisorMapMetricRule }))
    .filter(({ rule }) => Boolean(rule?.rule_title));

const ROWS_PER_PAGE_OPTIONS = [10, 25, 100];

const AdvisorMapsTable = ({ advisorMapsData }: Props) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const oauthTokenHeader: any = useSelector(selectUserToken);
  const userData = useSelector(selectUserData);
  // without this, the map can be viewed but none of its actions are offered
  const hasPermission = CheckPermissions(
    userData?.data?.products?.data_collection,
    "businesse_advisor",
    "create_edit_delete_advisor_maps_&_gap_rules",
  );
  const mapsToBrandsData = useSelector(
    (state: any) => state.advisor?.businessAdvisorMapsToBrandsData,
  );
  const [sort, setSort] = React.useState<{
    keytoSort: string;
    direction: SortDirection;
  }>({ keytoSort: "", direction: "asc" });

  const [brandsLoading, setBrandsLoading] = React.useState(false);
  const [openBrandsDialog, setOpenBrandsDialog] = React.useState(false);
  // captured from the clicked row so the dialog header survives a re-sort
  const [selectedMapName, setSelectedMapName] = React.useState("");
  const [selectedInterfaceName, setSelectedInterfaceName] = React.useState("");
  const [brandSortKey, setBrandSortKey] =
    React.useState<BrandSortKey>("brand_name");
  const [brandSortDirection, setBrandSortDirection] =
    React.useState<SortDirection>("asc");

  // Kept separate from `mapToDelete` so the dialog keeps its content while it
  // animates closed - clearing the row here makes it flip to the other wording
  // and grow a Yes button for the length of the fade.
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
  const [mapToDelete, setMapToDelete] = React.useState<AdvisorMapApiRow | null>(
    null,
  );
  const [deleting, setDeleting] = React.useState(false);
  // the list is pulled again after a delete - the rows on screen are stale
  // until it lands, so the table stands down for the skeleton meanwhile
  const [refreshing, setRefreshing] = React.useState(false);

  const rowsPerPageFromUrl = parseInt(
    searchParams.get("rowsPerPage") ?? "",
    10,
  );
  const tableRowsPerPage = ROWS_PER_PAGE_OPTIONS.includes(rowsPerPageFromUrl)
    ? rowsPerPageFromUrl
    : 10;

  const rowCount = advisorMapsData?.length ?? 0;
  const totalPages = Math.max(1, Math.ceil(rowCount / tableRowsPerPage));
  const pageFromUrl = parseInt(searchParams.get("page") ?? "", 10);
  const currentPage =
    pageFromUrl >= 1 && pageFromUrl <= totalPages ? pageFromUrl : 1;
  const tablePage = currentPage - 1;
  React.useEffect(() => {
    if (pageFromUrl === currentPage) return;
    setSearchParams(
      (prevParams) => {
        const params = new URLSearchParams(prevParams);
        params.set("page", String(currentPage));
        return params;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageFromUrl, currentPage]);

  const handleChangePage = (_: any, newPage: number) => {
    setSearchParams(
      (prevParams) => {
        const params = new URLSearchParams(prevParams);
        params.set("page", String(newPage + 1));
        params.set("rowsPerPage", String(tableRowsPerPage));
        return params;
      },
      { replace: true },
    );
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchParams(
      (prevParams) => {
        const params = new URLSearchParams(prevParams);
        params.set("rowsPerPage", event.target.value);
        params.set("page", "1");
        return params;
      },
      { replace: true },
    );
  };

  const buildMapParams = (row: AdvisorMapApiRow) => {
    const params = new URLSearchParams(searchParams);
    params.set("mapId", String(row.id));
    params.set("mapName", row.name);
    return params.toString();
  };

  // const handleRedirectToMapDetails = (row: AdvisorMapApiRow) => {
  //   navigate(`/data_collection/advisor/map_details?${buildMapParams(row)}`);
  // };

  const handleEditMap = (row: AdvisorMapApiRow) => {
    navigate(`/data_collection/advisor/edit_map?${buildMapParams(row)}`);
  };

  const handleDuplicateMap = (row: AdvisorMapApiRow) => {
    navigate(`/data_collection/advisor/duplicate_map?${buildMapParams(row)}`);
  };

  const handleConfirmDelete = async () => {
    // a map with brands connected is never deletable
    if (!mapToDelete || mapToDelete.brands_connected > 0) return;
    setDeleting(true);
    try {
      const isSuccess = await AdvisorMapsEndpointLogic.deleteAdvisorMap(
        oauthTokenHeader,
        mapToDelete.id,
      );
      if (isSuccess) {
        setOpenDeleteDialog(false);
        // pull the list again so the deleted row drops out
        setRefreshing(true);
        try {
          await AdvisorMapsEndpointLogic.getBusinessAdvisorMaps(
            oauthTokenHeader,
            searchParams.get("interfaceID"),
            searchParams.get("destinationTableIDMain"),
          );
        } finally {
          setRefreshing(false);
        }
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenBrandsDialog = async (row: AdvisorMapApiRow) => {
    if (!row.brands_connected) return;
    setSelectedMapName(row.name);
    setSelectedInterfaceName(row.backend_interface_name);
    // open straight away - the brand rows render as skeletons until they land
    setOpenBrandsDialog(true);
    setBrandsLoading(true);
    try {
      const isSuccess =
        await AdvisorMapsEndpointLogic.getBusinessAdvisorMapsToBrands(
          oauthTokenHeader,
          row.id,
        );
      // the endpoint logic already surfaced the error snackbar
      if (!isSuccess) setOpenBrandsDialog(false);
    } finally {
      setBrandsLoading(false);
    }
  };

  const handleCloseBrandsDialog = () => {
    setOpenBrandsDialog(false);
    setBrandSortKey("brand_name");
    setBrandSortDirection("asc");
  };

  const handleBrandHeaderClick = (key: BrandSortKey) => {
    if (brandSortKey === key) {
      setBrandSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setBrandSortKey(key);
      setBrandSortDirection("asc");
    }
  };

  const sortedBrands: AdvisorMapBrand[] = React.useMemo(() => {
    const brands: AdvisorMapBrand[] = Array.isArray(mapsToBrandsData?.brands)
      ? mapsToBrandsData.brands.filter(
          (brand: AdvisorMapBrand | null) => brand?.brand_id != null,
        )
      : [];
    if (!brands.length) return [];
    return [...brands].sort((a, b) => {
      const aValue = a[brandSortKey];
      const bValue = b[brandSortKey];
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === "string" && typeof bValue === "string") {
        return brandSortDirection === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      return brandSortDirection === "asc"
        ? Number(aValue) - Number(bValue)
        : Number(bValue) - Number(aValue);
    });
  }, [mapsToBrandsData, brandSortKey, brandSortDirection]);

  const handleCreateNewMap = () => {
    const currentParams = searchParams.toString();
    navigate(
      `/data_collection/advisor/create_new_map${
        currentParams ? `?${currentParams}` : ""
      }`,
    );
  };

  const handleHeaderClick = (header: (typeof tableHeaders)[number]) => {
    if (!header.sortable) return;
    setSort((prev) => ({
      keytoSort: header.KEY,
      direction:
        prev.keytoSort === header.KEY && prev.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const sortedRows = React.useMemo(() => {
    if (!sort.keytoSort) return advisorMapsData ?? [];

    return (advisorMapsData ?? []).slice().sort((a: any, b: any) => {
      // derivations is a list - sort by how many rules the map lists
      const aValue =
        sort.keytoSort === "derivations"
          ? getMetricRules(a).length
          : a[sort.keytoSort];
      const bValue =
        sort.keytoSort === "derivations"
          ? getMetricRules(b).length
          : b[sort.keytoSort];

      if (aValue === undefined || aValue === null || aValue === "") return 1;
      if (bValue === undefined || bValue === null || bValue === "") return -1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sort.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
    });
  }, [advisorMapsData, sort]);

  const paginatedRows = sortedRows.slice(
    tablePage * tableRowsPerPage,
    tablePage * tableRowsPerPage + tableRowsPerPage,
  );

  // the delete dialog is already closed by the time the refresh starts, so
  // nothing is lost by standing the whole list down for the skeleton
  if (refreshing) {
    return <AdvisorMapsTableSkeleton rows={tableRowsPerPage} />;
  }

  return (
    <>
      <PersistentErrorSnackbar />
      <Box display="flex" justifyContent="flex-end" sx={{ mb: 1 }}>
        <Button
          variant="outlined"
          color="secondary"
          sx={{ whiteSpace: "nowrap" }}
          onClick={handleCreateNewMap}
          data-tracker="952"
          disabled={!hasPermission}
        >
          Create new MAP
          <AddIcon sx={{ width: "20px", ml: "5px" }} />
        </Button>
      </Box>

      <TableContainer className="advisorMapsTable">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {tableHeaders.map((header) => (
                <TableCell key={header.id} align="left">
                  <TableSortLabel
                    active={sort.keytoSort === header.KEY}
                    direction={
                      sort.keytoSort === header.KEY ? sort.direction : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows && paginatedRows.length > 0 ? (
              paginatedRows.map((row) => {
                const metricRules = getMetricRules(row);

                return (
                  <TableRow
                    key={row.id}
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                        padding: "12px 16px",
                      },
                    }}
                  >
                    <TableCell align="left">
                      <Box display="flex" alignItems="center">
                        <span
                          //data-testid="advisor-map-name-link"
                          style={{
                            color: "#9C27B0",
                            //cursor: "pointer",
                          }}
                          // onClick={() => handleRedirectToMapDetails(row)}
                        >
                          {row.name}
                        </span>
                      </Box>
                    </TableCell>
                    <TableCell align="left">{row.id}</TableCell>
                    <TableCell align="left">{row.destination_table}</TableCell>
                    <TableCell align="left">
                      {/* with no brands there is nothing to list, so the count
                          is plain text and the dialog is never opened */}
                      {row.brands_connected > 0 ? (
                        <Typography
                          data-testid="advisor-brands-using-map-link"
                          data-tracker="945"
                          sx={{
                            color: "#9C27B0",
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontWeight: 500,
                            fontSize: "14px",
                            width: "fit-content",
                          }}
                          onClick={() => handleOpenBrandsDialog(row)}
                        >
                          {row.brands_connected}
                        </Typography>
                      ) : (
                        <Tooltip
                          title="No brands are using this map"
                          placement="top"
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              width: "fit-content",
                              cursor: "default",
                            }}
                          >
                            {row.brands_connected}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="left">
                      {metricRules.length > 0 ? (
                        <ul className="advisorDerivationList">
                          {metricRules.map(({ metric, rule }) => (
                            <li key={metric}>
                              <Typography
                                component="span"
                                sx={{ fontSize: "14px", fontWeight: 500 }}
                              >
                                {metric}
                              </Typography>
                              <Typography
                                component="span"
                                sx={{ fontSize: "14px" }}
                              >
                                {`: ${rule?.rule_title}`}
                              </Typography>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <Typography sx={{ fontSize: "14px" }}>
                          All Metrics from Source
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="left">
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Tooltip title="Edit" placement="top">
                          <IconButton
                            sx={{ width: "36px", height: "36px" }}
                            onClick={() => handleEditMap(row)}
                            data-tracker="946"
                            disabled={!hasPermission}
                          >
                            <EditIcon color="secondary" fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicate" placement="top">
                          <IconButton
                            sx={{ width: "36px", height: "36px" }}
                            onClick={() => handleDuplicateMap(row)}
                            data-tracker="948"
                            disabled={!hasPermission}
                          >
                            <ContentCopyIcon
                              color="secondary"
                              fontSize="small"
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete" placement="top">
                          <IconButton
                            sx={{ width: "36px", height: "36px" }}
                            onClick={() => {
                              setMapToDelete(row);
                              setOpenDeleteDialog(true);
                            }}
                            data-tracker="950"
                            disabled={!hasPermission}
                          >
                            <DeleteOutlineIcon
                              color="secondary"
                              fontSize="small"
                            />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={tableHeaders.length} align="center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ flex: 1 }}>
            <TablePagination
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              component="div"
              count={rowCount}
              rowsPerPage={tableRowsPerPage}
              page={tablePage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                ".MuiTablePagination-actions": {
                  display: "none",
                },
                // the spacer is "flex: 1 1 100%" and would push the label, select
                // and count across to the right - drop it so they stay bottom-left
                ".MuiTablePagination-spacer": {
                  display: "none",
                },
                ".MuiTablePagination-toolbar": {
                  paddingLeft: "16px",
                  justifyContent: "flex-start",
                },
                ".MuiTablePagination-displayedRows": {
                  marginLeft: "24px",
                },
              }}
            />
          </Box>
          <Pagination
            count={totalPages}
            page={currentPage}
            showFirstButton
            showLastButton
            className="pagination-button"
            color="secondary"
            size="small"
            onChange={(_, value) => handleChangePage(null, value - 1)}
          />
          {/* spacer keeps the pagination visually centered under the table */}
          <Box sx={{ flex: 1 }} />
        </Stack>
      </TableContainer>

      <Dialog
        open={openBrandsDialog}
        onClose={handleCloseBrandsDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            pb: 0.5,
            fontSize: "18px",
            lineHeight: 1.4,
            overflowWrap: "anywhere",
          }}
        >
          {mapsToBrandsData?.name || selectedMapName}
        </DialogTitle>

        <Stack direction="row" gap={2} sx={{ px: 3, pb: 1 }}>
          <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
            {selectedInterfaceName}
          </Typography>
          {brandsLoading ? (
            <Skeleton width={70} />
          ) : (
            <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
              {`${sortedBrands.length} Brands`}
            </Typography>
          )}
        </Stack>

        <DialogContent sx={{ maxHeight: "40vh", pt: 0 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: "#fff" }}>
                  <TableSortLabel
                    active={brandSortKey === "brand_name"}
                    direction={
                      brandSortKey === "brand_name" ? brandSortDirection : "asc"
                    }
                    onClick={() => handleBrandHeaderClick("brand_name")}
                  >
                    Brand Name
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ backgroundColor: "#fff", whiteSpace: "nowrap" }}
                >
                  <TableSortLabel
                    active={brandSortKey === "brand_id"}
                    direction={
                      brandSortKey === "brand_id" ? brandSortDirection : "asc"
                    }
                    onClick={() => handleBrandHeaderClick("brand_id")}
                  >
                    Brand ID
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {brandsLoading ? (
                Array.from({ length: 8 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton width="80%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton width={50} />
                    </TableCell>
                  </TableRow>
                ))
              ) : sortedBrands.length > 0 ? (
                sortedBrands.map((brand) => (
                  <TableRow key={brand.brand_id}>
                    <TableCell sx={{ overflowWrap: "anywhere" }}>
                      {brand.brand_name}
                    </TableCell>
                    <TableCell>{brand.brand_id}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    No brands found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleCloseBrandsDialog}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ overflowWrap: "anywhere" }}>
          {`Delete ${mapToDelete?.name ?? "map"}?`}
        </DialogTitle>
        <DialogContent>
          {/* a map still in use gets the stronger warning */}
          {(mapToDelete?.brands_connected ?? 0) > 0 ? (
            <Typography sx={{ fontSize: "14px" }}>
              This Map Has Brands Connected, Deleting a Map in Use Would Break
              Their Reporting.
            </Typography>
          ) : (
            <Typography sx={{ fontSize: "14px" }}>
              Are you sure you want to delete this map? This action cannot be
              undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            color="secondary"
            disabled={deleting}
            onClick={() => setOpenDeleteDialog(false)}
          >
            Cancel
          </Button>
          {/* a map in use cannot be deleted, so it is left with only a way out */}
          {(mapToDelete?.brands_connected ?? 0) === 0 && (
            <Button
              variant="contained"
              color="secondary"
              disabled={deleting}
              onClick={handleConfirmDelete}
              data-tracker="951"
            >
              Yes
              {deleting && (
                <CircularProgress
                  size={16}
                  color="inherit"
                  sx={{ ml: "8px" }}
                />
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdvisorMapsTable;
